/**
 * Driving a reconciliation to completion, one bounded request at a time.
 *
 * The first production run of the desk-wide reconciliation never returned. The
 * browser reported "No 'Access-Control-Allow-Origin' header", and CORS was not
 * the fault: preflight on that exact route answers correctly and even a 401
 * from it carries the header. The request was killed in flight by the
 * platform's ingress, and a killed connection carries no headers at all — so a
 * timeout reaches the browser wearing a CORS error's clothes.
 *
 * Reconciling a real package is seconds of work on one CPU (OCR on a scanned
 * PDF, an anchor sweep over a workbook), so the desk is minutes and no single
 * request can hold it. The server now answers within a time budget and names
 * the records it never started; this drives the loop, handing those ids back
 * until the run is complete, and accumulates the batches into the one report
 * the screen renders.
 *
 * It terminates by construction: every call starts at least one deal, so the
 * not-started set strictly shrinks. `MAX_ROUNDS` is a guard against a server
 * that stops honouring that, not part of the mechanism — reaching it returns
 * what was done with the rest still named, rather than spinning.
 */
import type { ReconcileResponse } from '@/services/ingestion'

/** Far above any real desk: 25 live deals converge in one or two rounds. */
export const MAX_ROUNDS = 50

/** The count the server opens its note with when packages could not be read. */
const REUPLOAD_COUNT = /^\d+ deals? could not be re-read/

/** Two batches of one run, read as one. Later batches win on run-level state. */
export function mergeReconcileBatches(
  first: ReconcileResponse,
  next: ReconcileResponse,
): ReconcileResponse {
  const totals: Record<string, number> = { ...first.totals }
  for (const [key, value] of Object.entries(next.totals)) {
    totals[key] = (totals[key] ?? 0) + value
  }
  const skipped: Record<string, number> = { ...first.skipped }
  for (const [key, value] of Object.entries(next.skipped)) {
    skipped[key] = (skipped[key] ?? 0) + value
  }
  // One deal, one entry. Two intake records can convert into one deal — a
  // thread, a broker re-sending with the deck attached — and the server can
  // only see the ones inside its own batch, so the last defence against the
  // same loan appearing twice in a report is here.
  const seen = new Set(first.deals.map((d) => d.loanId))
  const needsReupload = [...(first.needsReupload ?? []), ...(next.needsReupload ?? [])]
  return {
    ...next,
    // Each batch writes its own count of unreadable packages into its note;
    // the run's note carries the run's count, the one the list beneath shows.
    note: needsReupload.length
      ? ([next.note, first.note].find((n) => REUPLOAD_COUNT.test(n ?? '')) ?? next.note ?? '').replace(
          REUPLOAD_COUNT,
          `${needsReupload.length} deal${needsReupload.length === 1 ? '' : 's'} could not be re-read`,
        )
      : next.note,
    // `examined` counts the records each call considered, and a resumed call
    // considers only what was left. The run examined what the first call saw.
    examined: first.examined,
    deals: [...first.deals, ...next.deals.filter((d) => !seen.has(d.loanId))],
    failed: [...first.failed, ...next.failed],
    needsReupload,
    totals,
    skipped,
    elapsedSeconds: Number(((first.elapsedSeconds ?? 0) + (next.elapsedSeconds ?? 0)).toFixed(2)),
  }
}

export type ReconcileProgress = {
  /** Deals reconciled so far, across every batch. */
  done: number
  /** Records the last batch had not started. */
  remaining: number
  /** Which request this is, from 1. */
  round: number
}

type Options = {
  apply: boolean
  /** One bounded call. Injected so the loop is testable without a transport. */
  call: (input: { apply: boolean; ids?: string[] }) => Promise<ReconcileResponse>
  onProgress?: (progress: ReconcileProgress) => void
  /** Restrict the whole run to these records; batching still applies within it. */
  ids?: string[]
}

/**
 * Call until the run is complete, accumulating every batch into one report.
 *
 * An apply commits batch by batch, so an interrupted run is not lost work: the
 * deals it finished stay finished, and the next run starts where it stopped.
 * That is the same property that makes the loop safe to re-run at all.
 */
export async function runReconciliation({
  apply,
  call,
  onProgress,
  ids,
}: Options): Promise<ReconcileResponse> {
  let accumulated: ReconcileResponse | null = null
  let pending: string[] | undefined = ids

  for (let round = 1; round <= MAX_ROUNDS; round += 1) {
    const batch = await call({ apply, ids: pending })
    accumulated = accumulated ? mergeReconcileBatches(accumulated, batch) : batch
    onProgress?.({
      done: accumulated.deals.length,
      remaining: batch.remaining?.length ?? 0,
      round,
    })
    if (batch.complete || !batch.remaining?.length) return accumulated
    pending = batch.remaining
  }
  // Only reachable if the server stopped shrinking `remaining`. Report what was
  // done and leave the rest named, rather than looping on it.
  return accumulated as ReconcileResponse
}
